import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MaterialSymbol } from "../../icons/MaterialSymbol";
import type {
  ControlRowProps,
  ControlActionProps,
  ControlIcon,
} from "./SettingsControlParts.types";

const ICONS: Record<ControlIcon, string> = {
  profile: "account_circle",
  privacy: "shield",
  contact: "contact_mail",
  cv: "description",
  link: "link",
  memory: "psychology",
  account: "person_remove",
  help: "help_outline",
  connection: "hub",
  download: "download",
};
export function ControlStack({
  children,
  gap = 20,
}: {
  children: ReactNode;
  gap?: number;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap, minWidth: 0 }}>
      {children}
    </div>
  );
}
export function ControlCopy({
  children,
  title = false,
}: {
  children: ReactNode;
  title?: boolean;
}) {
  return (
    <p
      dir="auto"
      style={{
        margin: 0,
        color: title ? "var(--text-primary)" : "var(--text-secondary)",
        fontSize: title ? 16 : 14,
        fontWeight: title ? 500 : 400,
        lineHeight: title ? "24px" : "21px",
        overflowWrap: "anywhere",
      }}
    >
      {children}
    </p>
  );
}
export function ControlHeading({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <header style={{ marginBlockEnd: 4 }}>
      <h1
        style={{
          margin: "0 0 8px",
          fontSize: 26,
          fontWeight: 400,
          lineHeight: "34px",
          color: "var(--text-primary)",
        }}
      >
        {title}
      </h1>
      <ControlCopy>{description}</ControlCopy>
    </header>
  );
}
export function ControlSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h2
        style={{
          margin: "0 0 8px",
          fontSize: 14,
          lineHeight: "22px",
          fontWeight: 500,
          color: "var(--text-secondary)",
        }}
      >
        {title}
      </h2>
      <div style={{ borderBlock: "1px solid var(--input-border)" }}>
        {children}
      </div>
    </section>
  );
}
export function ControlCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children?: ReactNode;
}) {
  return (
    <ControlStack gap={12}>
      <ControlCopy title>{title}</ControlCopy>
      {description ? <ControlCopy>{description}</ControlCopy> : null}
      {children}
    </ControlStack>
  );
}
export function ControlRow({
  title,
  description,
  value,
  icon,
  onPress,
  disabled,
  selected,
  trailing,
}: ControlRowProps) {
  const content = (
    <>
      {icon ? (
        <span
          aria-hidden
          style={{
            display: "flex",
            width: 30,
            flexShrink: 0,
            color: "var(--text-secondary)",
          }}
        >
          <MaterialSymbol name={ICONS[icon]} size={22} />
        </span>
      ) : null}
      <span style={{ flex: 1, minWidth: 0, textAlign: "start" }}>
        <span
          dir="auto"
          style={{
            display: "block",
            fontSize: 15,
            fontWeight: 500,
            lineHeight: "22px",
            color: "var(--text-primary)",
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </span>
        {description ? (
          <span
            dir="auto"
            style={{
              display: "block",
              marginBlockStart: 3,
              fontSize: 13,
              lineHeight: "19px",
              color: "var(--text-secondary)",
              overflowWrap: "anywhere",
            }}
          >
            {description}
          </span>
        ) : null}
      </span>
      {value ? (
        <span
          style={{
            fontSize: 12,
            lineHeight: "18px",
            maxWidth: "32%",
            textAlign: "end",
            color: "var(--text-secondary)",
          }}
        >
          {value}
        </span>
      ) : null}
      {trailing}
      {selected ? (
        <MaterialSymbol name="check" size={21} />
      ) : onPress && selected === undefined ? (
        <MaterialSymbol
          name="chevron_right"
          className="tabbio-rtl-flip"
          size={19}
          style={{ color: "var(--text-tertiary)" }}
        />
      ) : null}
    </>
  );
  const style = {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    minHeight: 68,
    padding: "14px 10px",
    border: 0,
    borderRadius: 10,
    color: "var(--text-primary)",
    background: selected ? "var(--hover)" : "transparent",
    fontFamily: "inherit",
    opacity: disabled ? 0.45 : 1,
    boxSizing: "border-box" as const,
  };
  return onPress ? (
    <button
      type="button"
      aria-label={title}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onPress}
      style={{
        ...style,
        cursor: disabled ? "default" : "pointer",
        transition: "background 150ms",
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = "var(--hover)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = selected
          ? "var(--hover)"
          : "transparent";
      }}
    >
      {content}
    </button>
  ) : (
    <div style={style}>{content}</div>
  );
}
export function ControlAction({
  title,
  onPress,
  disabled,
  primary,
}: ControlActionProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onPress}
      style={{
        minHeight: 44,
        borderRadius: 999,
        padding: "10px 20px",
        border: primary
          ? "1px solid transparent"
          : "1px solid var(--input-border)",
        background: primary ? "var(--text-primary)" : "transparent",
        color: primary ? "var(--bg)" : "var(--text-primary)",
        fontSize: 14,
        lineHeight: "20px",
        fontWeight: 500,
        fontFamily: "inherit",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {title}
    </button>
  );
}
export function ControlHelp({
  title,
  onPress,
}: {
  title: string;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      style={{
        alignSelf: "flex-start",
        display: "flex",
        alignItems: "center",
        gap: 8,
        minHeight: 44,
        border: 0,
        padding: "8px 0",
        background: "transparent",
        color: "var(--text-secondary)",
        font: "inherit",
        fontSize: 13,
        textAlign: "start",
        cursor: "pointer",
      }}
    >
      <MaterialSymbol name="help_outline" size={18} />
      <span style={{ textDecoration: "underline", textUnderlineOffset: 3 }}>
        {title}
      </span>
    </button>
  );
}
export function ControlToggle({
  title,
  description,
  checked,
  onChange,
  disabled,
}: {
  title: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <ControlRow
      title={title}
      description={description}
      trailing={
        <button
          type="button"
          role="switch"
          aria-label={title}
          aria-checked={checked}
          disabled={disabled}
          onClick={() => onChange(!checked)}
          style={{
            border: 0,
            padding: 8,
            background: "transparent",
            display: "flex",
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.4 : 1,
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: checked ? "flex-end" : "flex-start",
              width: 36,
              height: 22,
              border: "1px solid var(--input-border)",
              borderRadius: 999,
              padding: 2,
              background: checked
                ? "var(--text-primary)"
                : "var(--hover-strong)",
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: checked ? "var(--bg)" : "var(--text-secondary)",
              }}
            />
          </span>
        </button>
      }
    />
  );
}
export function ControlQueryState({
  loading,
  error,
  retry,
}: {
  loading: boolean;
  error: boolean;
  retry: () => unknown;
}) {
  const { t } = useTranslation("common");
  if (error)
    return (
      <div role="alert" style={{ paddingBlock: 16 }}>
        <ControlStack gap={12}>
          <ControlCopy>{t("settings.dataManagement.loadFailed")}</ControlCopy>
          <ControlAction
            title={t("settings.dataManagement.retry")}
            onPress={() => void retry()}
          />
        </ControlStack>
      </div>
    );
  return loading ? (
    <div role="status" style={{ paddingBlock: 16 }}>
      <ControlCopy>{t("labels.loading")}</ControlCopy>
    </div>
  ) : null;
}
export type { ControlIcon };
